import React, { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";

// Import modal
import UnlockUserModal from "../modals/UnlockUserModal";

const SupportUserAccess = () => {
  const routes = all_routes;
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterRole, setFilterRole] = useState("all");
  const [selectedUser, setSelectedUser] = useState<any>(null);

  // Enhanced mock data for testing
  const mockAccessLogs = [
    {
      id: 1,
      userId: "USR001",
      name: "John Smith",
      email: "john.smith@school.edu",
      role: "Teacher",
      status: "Active",
      lastLogin: "2024-07-20 09:30",
      loginAttempts: 1,
      accountLocked: false,
      ipAddress: "192.168.1.45",
      failedAttempts: 1,
      lastAttempt: "Today 9:30 AM",
      lastIP: "192.168.1.45",
      lastDevice: "Windows Chrome",
    },
    {
      id: 2,
      userId: "USR002",
      name: "Sarah Johnson",
      email: "sarah.j@school.edu",
      role: "Student",
      status: "Active",
      lastLogin: "2024-07-20 08:15",
      loginAttempts: 1,
      accountLocked: false,
      ipAddress: "192.168.1.78",
      failedAttempts: 1,
      lastAttempt: "Today 8:15 AM",
      lastIP: "192.168.1.78",
      lastDevice: "iPhone Safari",
    },
    {
      id: 3,
      userId: "USR003",
      name: "Mike Wilson",
      email: "mike.wilson@parent.com",
      role: "Parent",
      status: "Locked",
      lastLogin: "2024-07-18 16:45",
      loginAttempts: 5,
      accountLocked: true,
      ipAddress: "192.168.1.92",
      lockStatus: "Account Locked",
      lockDate: "2024-07-19 10:00",
      lockReason: "Multiple failed login attempts",
      failedAttempts: 5,
      lastAttempt: "Today 2:30 PM",
      lastIP: "192.168.1.92",
      lastDevice: "Windows Edge",
    },
    {
      id: 4,
      userId: "USR004",
      name: "Emma Davis",
      email: "emma.davis@school.edu",
      role: "Admin",
      status: "Inactive",
      lastLogin: "2024-07-15 14:22",
      loginAttempts: 0,
      accountLocked: false,
      ipAddress: "192.168.1.15",
      failedAttempts: 0,
      lastAttempt: "July 15 2:22 PM",
      lastIP: "192.168.1.15",
      lastDevice: "MacOS Safari",
    },
  ];

  const mockRecentActions = [
    {
      id: 1,
      action: "Password Reset",
      user: "Alice Johnson",
      performer: "Support Team",
      timestamp: "2024-07-20 10:15",
      details: "Password reset requested and completed",
    },
    {
      id: 2,
      action: "Account Unlocked",
      user: "Bob Smith",
      performer: "Admin",
      timestamp: "2024-07-20 09:45",
      details: "Account unlocked after failed login attempts",
    },
    {
      id: 3,
      action: "Role Updated",
      user: "Carol Davis",
      performer: "Super Admin",
      timestamp: "2024-07-19 16:30",
      details: "Role changed from Student to Teacher",
    },
  ];

  const handleUnlockUser = (user: any) => {
    setSelectedUser(user);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "badge-soft-success";
      case "Inactive":
        return "badge-soft-secondary";
      case "Locked":
        return "badge-soft-danger";
      case "Suspended":
        return "badge-soft-warning";
      default:
        return "badge-soft-secondary";
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case "Admin":
        return "badge-soft-danger";
      case "Teacher":
        return "badge-soft-success";
      case "Student":
        return "badge-soft-primary";
      case "Parent":
        return "badge-soft-warning";
      default:
        return "badge-soft-secondary";
    }
  };

  const filteredLogs = mockAccessLogs.filter((log) => {
    const statusMatch = filterStatus === "all" || log.status === filterStatus;
    const roleMatch = filterRole === "all" || log.role === filterRole;
    return statusMatch && roleMatch;
  });

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.supportDashboard}>Support Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">
                    User Access Management
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="row">
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-success-transparent me-3">
                      <i className="ti ti-user-check"></i>
                    </div>
                    <div>
                      <h4>
                        {
                          mockAccessLogs.filter(
                            (log) => log.status === "Active"
                          ).length
                        }
                      </h4>
                      <p className="text-muted mb-0">Active Users</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-danger-transparent me-3">
                      <i className="ti ti-lock"></i>
                    </div>
                    <div>
                      <h4>
                        {
                          mockAccessLogs.filter((log) => log.accountLocked)
                            .length
                        }
                      </h4>
                      <p className="text-muted mb-0">Locked Accounts</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-warning-transparent me-3">
                      <i className="ti ti-alert-triangle"></i>
                    </div>
                    <div>
                      <h4>
                        {
                          mockAccessLogs.filter((log) => log.loginAttempts > 3)
                            .length
                        }
                      </h4>
                      <p className="text-muted mb-0">Failed Attempts</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-3 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-lg bg-info-transparent me-3">
                      <i className="ti ti-users"></i>
                    </div>
                    <div>
                      <h4>{mockAccessLogs.length}</h4>
                      <p className="text-muted mb-0">Total Users</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* User Access Logs */}
            <div className="col-lg-8">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title">User Access Control</h4>
                  <p className="text-muted">
                    Manage user access permissions and unlock accounts
                  </p>
                </div>

                {/* Filters */}
                <div className="card-body border-bottom">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Search Users</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="ti ti-search"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by name, email..."
                        />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Filter by Status</label>
                      <select
                        className="form-select"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Locked">Locked</option>
                        <option value="Suspended">Suspended</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Filter by Role</label>
                      <select
                        className="form-select"
                        value={filterRole}
                        onChange={(e) => setFilterRole(e.target.value)}
                      >
                        <option value="all">All Roles</option>
                        <option value="Admin">Admin</option>
                        <option value="Teacher">Teacher</option>
                        <option value="Student">Student</option>
                        <option value="Parent">Parent</option>
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">&nbsp;</label>
                      <button className="btn btn-outline-secondary w-100">
                        <i className="ti ti-filter me-1"></i>Reset
                      </button>
                    </div>
                  </div>
                </div>

                {/* Access Logs Table */}
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Role</th>
                          <th>Status</th>
                          <th>Last Login</th>
                          <th>Login Attempts</th>
                          <th>IP Address</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLogs.map((log) => (
                          <tr key={log.id}>
                            <td>
                              <div>
                                <h6 className="mb-0">{log.name}</h6>
                                <small className="text-muted">
                                  {log.email}
                                </small>
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge ${getRoleColor(log.role)}`}
                              >
                                {log.role}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge ${getStatusColor(
                                  log.status
                                )}`}
                              >
                                {log.status}
                              </span>
                            </td>
                            <td>{log.lastLogin}</td>
                            <td>
                              <span
                                className={
                                  log.loginAttempts > 3
                                    ? "text-danger"
                                    : "text-muted"
                                }
                              >
                                {log.loginAttempts}
                              </span>
                            </td>
                            <td>
                              <small className="text-muted">
                                {log.ipAddress}
                              </small>
                            </td>
                            <td>
                              <div className="dropdown">
                                <button
                                  className="btn btn-white btn-sm dropdown-toggle"
                                  data-bs-toggle="dropdown"
                                >
                                  Actions
                                </button>
                                <ul className="dropdown-menu">
                                  <li>
                                    <button className="dropdown-item">
                                      <i className="ti ti-eye me-2"></i>View
                                      Details
                                    </button>
                                  </li>
                                  <li>
                                    <button
                                      className={`dropdown-item ${
                                        log.accountLocked ? "text-success" : ""
                                      }`}
                                      disabled={!log.accountLocked}
                                      data-bs-toggle="modal"
                                      data-bs-target="#unlockUserModal"
                                      onClick={() =>
                                        log.accountLocked &&
                                        handleUnlockUser(log)
                                      }
                                    >
                                      <i className="ti ti-lock-open me-2"></i>
                                      Unlock Account
                                    </button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item">
                                      <i className="ti ti-key me-2"></i>Reset
                                      Password
                                    </button>
                                  </li>
                                  <li>
                                    <hr className="dropdown-divider" />
                                  </li>
                                  <li>
                                    <button className="dropdown-item">
                                      <i className="ti ti-history me-2"></i>
                                      Login History
                                    </button>
                                  </li>
                                  <li>
                                    <button className="dropdown-item text-warning">
                                      <i className="ti ti-ban me-2"></i>Suspend
                                      User
                                    </button>
                                  </li>
                                </ul>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Actions & Tools */}
            <div className="col-lg-4">
              {/* Quick Actions */}
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">Quick Actions</h5>
                </div>
                <div className="card-body">
                  <div className="d-grid gap-2">
                    <button className="btn btn-primary btn-sm">
                      <i className="ti ti-lock-open me-1"></i>Bulk Unlock
                      Accounts
                    </button>
                    <button className="btn btn-warning btn-sm">
                      <i className="ti ti-key me-1"></i>Mass Password Reset
                    </button>
                    <button className="btn btn-info btn-sm">
                      <i className="ti ti-download me-1"></i>Export Access
                      Report
                    </button>
                    <button className="btn btn-success btn-sm">
                      <i className="ti ti-user-plus me-1"></i>Create Support
                      Ticket
                    </button>
                  </div>
                </div>
              </div>

              {/* Recent Actions */}
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">Recent Actions</h5>
                </div>
                <div className="card-body">
                  {mockRecentActions.map((action) => (
                    <div
                      key={action.id}
                      className="d-flex align-items-start mb-3 p-2 border rounded"
                    >
                      <div className="avatar avatar-sm bg-primary-transparent me-3">
                        <i className="ti ti-settings"></i>
                      </div>
                      <div className="flex-fill">
                        <h6 className="mb-1">{action.action}</h6>
                        <p className="text-muted mb-1 small">
                          {action.details}
                        </p>
                        <small className="text-muted">
                          User: {action.user}
                          <br />
                          By: {action.performer}
                          <br />
                          {action.timestamp}
                        </small>
                      </div>
                    </div>
                  ))}

                  <Link to="#" className="btn btn-outline-primary btn-sm w-100">
                    View All Actions
                  </Link>
                </div>
              </div>

              {/* System Tools */}
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">System Tools</h5>
                </div>
                <div className="card-body">
                  <div className="d-grid gap-2">
                    <button className="btn btn-outline-danger btn-sm">
                      <i className="ti ti-shield-check me-1"></i>Security Audit
                    </button>
                    <button className="btn btn-outline-warning btn-sm">
                      <i className="ti ti-database me-1"></i>Clear Cache
                    </button>
                    <button className="btn btn-outline-info btn-sm">
                      <i className="ti ti-backup me-1"></i>Backup Users
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unlock User Modal */}
      <UnlockUserModal user={selectedUser} />
    </>
  );
};

export default SupportUserAccess;
